import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import CommonModal from "Components/CommonModal";
import SalesCalendar from "Components/SalesCalendar";
import SalesDailyPanel from "Components/Sales/SalesDailyPanel";
import { catchError } from "JS/common";
import { getSalesCalendar } from "JS/API";

const styles = {
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
  },
  titleSection: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  monthTitle: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#1f2937",
    margin: 0,
  },
  navButtons: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  navButton: {
    width: "28px",
    height: "28px",
    backgroundColor: "#f3f4f6",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#6b7280",
    transition: "all 0.15s ease",
  },
  cards: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: "14px",
    marginBottom: "20px",
  },
  card: {
    backgroundColor: "#ffffff",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "#e5e7eb",
    borderRadius: "12px",
    padding: "18px 20px",
  },
  cardPending: {
    borderColor: "#2563eb",
  },
  cardLabel: {
    fontSize: "13px",
    color: "#6b7280",
    marginBottom: "6px",
  },
  cardValue: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#1f2937",
    lineHeight: 1.15,
    fontVariantNumeric: "tabular-nums",
  },
  subRefund: { fontSize: "12px", color: "#dc2626", marginTop: "6px" },
  subReal: { fontSize: "12px", color: "#1d4ed8", marginTop: "4px" },
  subNet: { fontSize: "12px", color: "#059669", marginTop: "4px" },
  subNote: { fontSize: "11px", color: "#9ca3af", marginTop: "4px" },
  subGray: { fontSize: "12px", color: "#6b7280", marginTop: "6px" },
  calendarCard: {
    backgroundColor: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    padding: "18px 20px",
  },
  loadingOverlay: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "100px",
    color: "#9ca3af",
    fontSize: "14px",
  },
};


const pad = (n) => String(n).padStart(2, "0");

const defaultSelectedDate = (year, month, daily) => {
  const today = new Date();
  if (today.getFullYear() === year && today.getMonth() + 1 === month) {
    return `${year}-${pad(month)}-${pad(today.getDate())}`;
  }
  const withSales = (daily || []).filter((d) => (d.paid_amount || d.amount) > 0).map((d) => d.date).sort();
  return withSales.length ? withSales[withSales.length - 1] : `${year}-${pad(month)}-01`;
};

const handleNavButtonHover = (e, isEnter) => {
  e.currentTarget.style.backgroundColor = isEnter ? "#e5e7eb" : "#f3f4f6";
};

const SalesCalendarTab = ({ year, month, service, updateParams }) => {
  const navigate = useNavigate();

  const [calendarData, setCalendarData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);

  const [alert, setAlert] = useState("");
  const [alertBox, setAlertBox] = useState({ mode: "", context: "", bool: false });

  const loadCalendarData = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getSalesCalendar(year, month, service);
      if (typeof result !== "object") {
        return catchError(result, navigate, setAlertBox, setAlert);
      }
      setCalendarData(result.data);
      setSelectedDate((prev) => {
        const prefix = `${year}-${pad(month)}-`;
        if (prev && prev.startsWith(prefix)) return prev;
        return defaultSelectedDate(year, month, result.data?.daily);
      });
    } catch (error) {
      catchError(error, navigate, setAlertBox, setAlert);
    } finally {
      setIsLoading(false);
    }
  }, [year, month, service, navigate]);

  const handlePrevMonth = useCallback(() => {
    if (month === 1) updateParams({ year: year - 1, month: 12 });
    else updateParams({ month: month - 1 });
  }, [year, month, updateParams]);

  const handleNextMonth = useCallback(() => {
    if (month === 12) updateParams({ year: year + 1, month: 1 });
    else updateParams({ month: month + 1 });
  }, [year, month, updateParams]);

  const handleDateClick = useCallback((date) => {
    setSelectedDate(date);
  }, []);

  const handleAlertOk = useCallback(() => {
    if (alert === "logout") navigate("/");
  }, [alert, navigate]);

  useEffect(() => {
    loadCalendarData();
  }, [loadCalendarData]);

  const summary = calendarData?.summary;
  const pendingAmount = summary?.by_status?.ready?.amount || 0;

  return (
    <div>
      <div style={styles.header}>
        <div style={styles.titleSection}>
          <h2 style={styles.monthTitle}>{year}년 {month}월</h2>
          <div style={styles.navButtons}>
            <button
              type="button"
              aria-label="이전 달"
              style={styles.navButton}
              onClick={handlePrevMonth}
              onMouseEnter={(e) => handleNavButtonHover(e, true)}
              onMouseLeave={(e) => handleNavButtonHover(e, false)}
            >
              <FaChevronLeft size={12} />
            </button>
            <button
              type="button"
              aria-label="다음 달"
              style={styles.navButton}
              onClick={handleNextMonth}
              onMouseEnter={(e) => handleNavButtonHover(e, true)}
              onMouseLeave={(e) => handleNavButtonHover(e, false)}
            >
              <FaChevronRight size={12} />
            </button>
          </div>
        </div>
      </div>

      {!isLoading && calendarData && (
        <div style={styles.cards}>
          <div style={styles.card}>
            <div style={styles.cardLabel}>{month}월 총 매출액</div>
            <div style={styles.cardValue}>{(summary?.total_amount || 0).toLocaleString()} 원</div>
            {summary?.total_refund_amount > 0 && (
              <div style={styles.subRefund}>환불 -{summary.total_refund_amount.toLocaleString()}원</div>
            )}
            {summary?.total_refund_amount > 0 && (
              <div style={styles.subReal}>
                실 매출 {((summary?.total_amount || 0) - (summary?.total_refund_amount || 0)).toLocaleString()}원
              </div>
            )}
            {summary?.net_revenue != null && (
              <div style={styles.subNet}>
                순매출 {summary.net_revenue.toLocaleString()}원
                <span style={{ color: "#9ca3af", marginLeft: "4px", fontSize: "11px" }}>(VAT,관납료 제외)</span>
              </div>
            )}
            {summary?.by_status?.partial_refund?.amount > 0 && (
              <div style={styles.subNote}>
                ※ 부분 환불 {summary.by_status.partial_refund.amount.toLocaleString()}원 순매출 미차감 (항목 구분 불가)
              </div>
            )}
          </div>
          <div style={styles.card}>
            <div style={styles.cardLabel}>{month}월 결제 건수</div>
            <div style={styles.cardValue}>{(summary?.total_count || 0).toLocaleString()} 건</div>
          </div>
          <div style={styles.card}>
            <div style={styles.cardLabel}>취소 건수</div>
            <div style={styles.cardValue}>{(summary?.by_status?.canceled?.count || 0).toLocaleString()} 건</div>
          </div>
          <div style={styles.card}>
            <div style={styles.cardLabel}>환불 건수</div>
            <div style={styles.cardValue}>{(summary?.total_refund_count || 0).toLocaleString()} 건</div>
            {summary?.total_refund_count > 0 && (
              <div style={styles.subGray}>
                {summary?.by_status?.refund?.count > 0 && <span>전체환불 {summary.by_status.refund.count}건</span>}
                {summary?.by_status?.refund?.count > 0 && summary?.by_status?.partial_refund?.count > 0 && <span> / </span>}
                {summary?.by_status?.partial_refund?.count > 0 && <span>부분 환불 {summary.by_status.partial_refund.count}건</span>}
              </div>
            )}
          </div>
          <div style={{ ...styles.card, ...(pendingAmount > 0 ? styles.cardPending : {}) }}>
            <div style={styles.cardLabel}>미수금 (대기)</div>
            <div style={styles.cardValue}>{pendingAmount.toLocaleString()} 원</div>
            <div style={styles.subGray}>대기 {(summary?.by_status?.ready?.count || 0).toLocaleString()}건</div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div style={styles.loadingOverlay}>로딩 중...</div>
      ) : (
        <div style={styles.calendarCard}>
          <SalesCalendar
            year={year}
            month={month}
            dailyData={calendarData?.daily || []}
            selectedDate={selectedDate}
            onDateClick={handleDateClick}
          />
        </div>
      )}

      {selectedDate && (
        <SalesDailyPanel
          key={selectedDate}
          date={selectedDate}
          service={service}
          onRefreshCalendar={loadCalendarData}
        />
      )}

      {alertBox.bool && (
        <CommonModal setModal={setAlertBox} modal={alertBox} okFn={handleAlertOk} />
      )}
    </div>
  );
};

export default SalesCalendarTab;
